import { Button, Checkbox, FormControlLabel, Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import DeleteIcon from "@material-ui/icons/Delete";
import Alert from "@material-ui/lab/Alert";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import CustomInput from "components/CustomInput";
import DropdownFilter from "components/DropdownFilter";
import LoadingSpinner from "components/LoadingSpinner";
import { formTypes } from "features/Forms/formBuilder/formBuilderConsts";
import {
    useGetActivityItemMaterialsQuery,
    useGetAllActivityItemsQuery,
    useGetAllActivityTypesQuery,
    useGetActivityFieldsTypesQuery,
} from "features/activity/activitySlice";
import MechanicalSLAFields from "features/clients/contracts/MechanicalWorkConfig/components/MechanicalSLAFields";
import { ruleFieldType } from "features/clients/contracts/MechanicalWorkConfig/consts";
import { useEffect, useState } from "react";
import { ActivityItemPricingType } from "../PricingManagementConsts";
import { isNameTakenBy } from "../utils/nameUtils";
import { CREATE_ACTIVITY_PRICING_STEP_WARNINGS } from "../CreateActivityAndPricing/createActivityAndPricingConsts";
import { useActivityItemDetailsFormStyles } from "./ActivityItemDetailsFormStyle";
import ActivityStatusAliasView from "features/activityStatusAlias";

const Section = ({ classes, title, children }) => (
    <Grid item className={classes.section}>
        <div className={classes.sectionTitle}>{title}</div>
        {children}
    </Grid>
);

const ActivityItemDetailsForm = ({
    activityItem,
    activityToEdit,
    setActivityToEdit,
    activityParamTypes = [],
}) => {
    const classes = useActivityItemDetailsFormStyles();

    const { data: allActivityItems = [], isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();

    const currentName = activityToEdit?.name || '';
    const isNameTaken = isNameTakenBy(allActivityItems, currentName, activityItem?.id);

    const activityNameSuggestions = currentName.length >= 2
        ? [...new Set(allActivityItems.map(item => item.name).filter(name =>
            name.toLowerCase().includes(currentName.toLowerCase())
        ))]
        : [];

    let { data: activityTypes, isLoading: isLoadingActivityTypes } =
        useGetAllActivityTypesQuery();

    let { data: fieldTypes, isLoading: isLoadingFieldsTypes } = useGetActivityFieldsTypesQuery();

    const { data: materials = [], isLoading: isLoadingMaterials } = useGetActivityItemMaterialsQuery(
        activityItem?.id,
        { skip: !activityItem?.id }
    );

    const [localMaterials, setLocalMaterials] = useState(activityToEdit?.materials || []);
    const deletedMaterialIds = activityToEdit?.deletedMaterialIds || [];
    const showDeleteWarning = deletedMaterialIds.length > 0;

    const getValidMaterials = (materials) =>
        materials.filter(m => m.materialName?.trim());

    useEffect(() => {
        if (!activityItem?.id || isLoadingMaterials) return;

        const mappedMaterials = materials
            .filter(m => !deletedMaterialIds.includes(m.id))
            .map(m => {
                const existingMaterial = localMaterials.find(lm => lm.id === m.id);

                if (existingMaterial) {
                    return existingMaterial;
                }

                return {
                    id: m.id,
                    materialName: m.materialName,
                    unitOfMeasure: m.unitOfMeasure || ''
                };
            });

        const existingNewMaterials = localMaterials.filter(m => m.id === null);
        const mergedMaterials = [...mappedMaterials, ...existingNewMaterials];

        setLocalMaterials(mergedMaterials);
        setActivityToEdit({
            ...activityToEdit,
            materials: getValidMaterials(mergedMaterials)
        });

    }, [activityItem?.id, materials, isLoadingMaterials]);

    // have Number of Weeks selected by default
    useEffect(() => {
        if (activityItem?.id || !fieldTypes || fieldTypes.length === 0) return;

        const currentParamTypes = activityToEdit?.paramTypes || [];
        const weeksField = fieldTypes.find(ft => ft.paramType === ruleFieldType.WEEKS);

        if (weeksField) {
            const weeksAlreadySelected = currentParamTypes.some(
                (p) => p.paramType === ruleFieldType.WEEKS
            );

            if (!weeksAlreadySelected) {
                onChange('paramTypes', [...currentParamTypes, weeksField]);
            }
        }
    }, [fieldTypes, activityItem?.id]);

    activityTypes = activityTypes || [];

    const showTypeAlert = activityToEdit && activityItem?.activityTypeId
        && activityToEdit.activityTypeId !== activityItem.activityTypeId;
    const showParamsAlert = activityToEdit?.paramTypes?.length < activityParamTypes.length;

    const onChange = (key, val) => {
        setActivityToEdit({ ...activityToEdit, [key]: val });
    };

    const getActivityTypeName = (id) => {
        let type = activityTypes.find((t) => t.id === id);
        return type?.name || "";
    };

    const onAddForm = () => {
        if (!activityItem?.id) return;
        const url = `/forms/activity?formType=${formTypes.Activity}&activityItemId=${activityItem?.id}`;
        window.open(url, '_blank');
    };

    const handleAddMaterial = () => {
        const newMaterialItem = {
            id: null,
            materialName: '',
            unitOfMeasure: ''
        };

        setLocalMaterials([...localMaterials, newMaterialItem]);
    };

    const handleUpdateMaterial = (index, field, value) => {
        const updatedMaterials = [...localMaterials];
        updatedMaterials[index] = {
            ...updatedMaterials[index],
            [field]: value
        };
        setLocalMaterials(updatedMaterials);
        setActivityToEdit({
            ...activityToEdit,
            materials: getValidMaterials(updatedMaterials),
            materialsModified: true
        });
    };

    const handleDeleteMaterial = (index) => {
        const updatedMaterials = [...localMaterials];
        const materialToDelete = updatedMaterials[index];

        // Track deleted material IDs to prevent them from reappearing
        const updatedDeletedIds = materialToDelete.id
            ? [...deletedMaterialIds, materialToDelete.id]
            : deletedMaterialIds;

        updatedMaterials.splice(index, 1);
        setLocalMaterials(updatedMaterials);
        setActivityToEdit({
            ...activityToEdit,
            materials: getValidMaterials(updatedMaterials),
            deletedMaterialIds: updatedDeletedIds,
            materialsModified: true
        });
    };

    const customFields = activityToEdit?.activityItemCustomFields || [];
    const defaultToggles = [
        { key: "noCharge", label: "No Charge?" },
        { key: "takePhotos", label: "Require Photos?" },
    ];
    const canAddForm = !!activityItem?.id;

    const showMaterialsConfig = activityToEdit?.pricingType === ActivityItemPricingType.BASE_PLUS_MATERIALS;
    const hasEmptyMaterialRow = localMaterials.some(
        m => !m.materialName?.trim()
    );
    if (isLoadingActivityTypes || !activityToEdit) {
        return null;
    }

    return (
        <Grid container direction="column">
            <Section classes={classes} title="Activity Details">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <DropdownFilter
                            title="Activity Name"
                            identifier="ddlActivityName"
                            value={activityToEdit?.name}
                            values={isLoadingActivityItems ? [] : activityNameSuggestions}
                            onSelect={(val) => {
                                const newIsNameTaken = isNameTakenBy(allActivityItems, val, activityItem?.id);
                                setActivityToEdit({ ...activityToEdit, name: val, nameIsAlreadyTaken: newIsNameTaken });
                            }}
                            withTypeAhead
                            freeSolo
                            required
                            valid={!isNameTaken}
                            touched={!!activityToEdit?.name}
                            helperText={isNameTaken ? CREATE_ACTIVITY_PRICING_STEP_WARNINGS.DUPLICATE_NAME : ''}
                            disabled={activityToEdit?.readonly}
                            classStyles={classes.dropdownStyle}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomInput
                            id="ddlActivityTypeId"
                            label="Activity Type"
                            elementType="dropdown"
                            values={activityTypes}
                            value={activityToEdit?.activityTypeId}
                            required={true}
                            showEmpty={true}
                            onChange={(val) => onChange("activityTypeId", Number(val))}
                            disabled={activityToEdit?.readonly}
                        />
                    </Grid>
                    {showTypeAlert && (
                        <Grid item xs={12}>
                            <Alert severity="warning" className={classes.alert}>
                                <>
                                    {"You are about to change the type of activity " + getActivityTypeName(activityItem?.activityTypeId) + "."}
                                    <br />
                                    {"This change will also apply to all existing assets. Are you sure you want to proceed?"}
                                </>
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </Section>

            {customFields.length > 0 && (
                <Section classes={classes} title="Custom Fields">
                    <Grid container spacing={2}>
                        {customFields.map((customField, index) => (
                            <Grid item xs={12} md={6} key={customField?.id || index}>
                                <CustomInput
                                    id="ddlCustomFieldLabel"
                                    label={`Custom Field ${index + 1}`}
                                    elementType="input"
                                    classStyles={classes.dropdownStyle}
                                    value={customField?.label}
                                    disabled
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Section>
            )}

            <Section classes={classes} title="Defaults">
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <ActivityStatusAliasView
                            readonly={activityToEdit?.readonly}
                            value={activityToEdit?.defaultStatus}
                            onChange={(val) => onChange("defaultStatus", val)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={1}>
                            {defaultToggles.map((toggle) => (
                                <Grid item xs={6} key={toggle.key}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!activityToEdit?.[toggle.key]}
                                                onChange={(value) =>
                                                    onChange(toggle.key, value.target.checked)
                                                }
                                                disabled={activityToEdit?.readonly}
                                            />
                                        }
                                        label={toggle.label}
                                        labelPlacement="start"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Section>

            <Section classes={classes} title="Mechanical SLA Logic">
                <MechanicalSLAFields
                    selectedParamTypes={activityToEdit?.paramTypes}
                    onChange={(updatedParamTypes) => onChange('paramTypes', updatedParamTypes)}
                    disabled={activityToEdit?.readonly}
                    showAlert={showParamsAlert}
                    className={classes.paramTypes}
                />
            </Section>

            <Section classes={classes} title="Pricing Unit">
                <Grid container spacing={2} direction="column">
                    <Grid item>
                        <ToggleButtonGroup
                            value={activityToEdit?.pricingType || ActivityItemPricingType.SIMPLE}
                            exclusive
                            onChange={(e, val) => { if (val !== null) onChange("pricingType", val); }}
                            disabled={activityToEdit?.readonly}
                            size="small"
                            sx={{
                                '& .Mui-selected': {
                                    backgroundColor: '#0090fe !important',
                                    color: '#fff !important',
                                },
                            }}
                        >
                            <ToggleButton value={ActivityItemPricingType.SIMPLE}>Flat Rate</ToggleButton>
                            <ToggleButton value={ActivityItemPricingType.BASE_PLUS_MATERIALS}>Base + Materials</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {showMaterialsConfig && (
                        <Grid item>
                            {showDeleteWarning && (
                                <Alert severity="warning" className={classes.alert}>
                                    Material deleted. All associated material prices will be automatically deleted when you save the activity.
                                </Alert>
                            )}
                            {isLoadingMaterials && activityItem?.id ? (
                                <LoadingSpinner loading={true} />
                            ) : (
                                <>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Material Name</TableCell>
                                                <TableCell>Unit of Measure</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {localMaterials.map((material, index) => (
                                                <TableRow key={material.id || `new-${index}`}>
                                                    <TableCell>
                                                        <CustomInput
                                                            id={`material-name-${index}`}
                                                            elementType="input"
                                                            placeholder="Material Name"
                                                            value={material.materialName}
                                                            onChange={(val) => handleUpdateMaterial(index, 'materialName', val)}
                                                            disabled={activityToEdit?.readonly}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <CustomInput
                                                            id={`material-unit-${index}`}
                                                            elementType="input"
                                                            placeholder="Unit of Measure (optional)"
                                                            value={material.unitOfMeasure || ''}
                                                            onChange={(val) => handleUpdateMaterial(index, 'unitOfMeasure', val)}
                                                            disabled={activityToEdit?.readonly}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteMaterial(index)}
                                                            disabled={activityToEdit?.readonly}
                                                            color="secondary"
                                                            title="Delete Material"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {!activityToEdit?.readonly && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddMaterial}
                                            disabled={hasEmptyMaterialRow}
                                            className={classes.addMaterialButton}
                                        >
                                            Add Material
                                        </Button>
                                    )}
                                </>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Section>

            {canAddForm && (
                <Grid item className={classes.fullRow}>
                    <Button
                        variant="outlined"
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        onClick={onAddForm}
                    >
                        Add Custom Form
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

export default ActivityItemDetailsForm;
